/**
 * step3.ts — "Review your piece" (flow step 3) building blocks.
 *
 * Step 3 is NOT a separate mounted screen: it's a "review mode" of the step-2
 * canvas (canvas.ts) so the assembled piece never unmounts and the transition
 * can morph in place. This module just builds the review-only chrome the canvas
 * composes + animates: the blue/image backdrop, the "YOUR CUSTOM …" header, the
 * order-overview sidebar, and the decorative paopao. All read-only.
 */

import { aggregateCharms, resolveChainLine, runningTotal, lengthSurcharge, type BuildState } from './state';
import type { CharmSize } from './geometry';
import { type Catalog } from './data';
import { renderDecorIcon } from './icons';
import { makeLoadingButton } from './loadingButton';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };
// Integer prices show no decimals ("€32"); fractional show two ("€2.50").
const priceLabel = (n: number) => '€' + (Number.isInteger(n) ? n : n.toFixed(2));
const initials = (t: string) => (t || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

export const REVIEW_BG = '#C8D7EA'; // light-blue review backdrop (same hue as the hover-state mix-blend)

const SIZE_WORD: Record<CharmSize, string> = { XS: 'XS', S: 'S', M: 'M', L: 'L', XL: 'XL' };

// Theme animated-button label (mirrors snippets/button.liquid → text-shadow wipe-up via btn--animate).
const animLabel = (text: string) => `<span class="btn__label"><span class="btn__label-inner">${text}</span></span>`;
// Two-row wipe for a label that includes an SVG (the text-shadow trick only duplicates
// text glyphs, not icons): stack two identical rows and slide up on hover. See .cfg-wipe CSS.
const animIconLabel = (text: string, icon: string) =>
  `<span class="cfg-wipe"><span class="cfg-wipe__inner">` +
    `<span class="cfg-wipe__row">${text}${icon}</span>` +
    `<span class="cfg-wipe__row" aria-hidden="true">${text}${icon}</span>` +
  `</span></span>`;

// Theme button class strings (mirrors snippets/button.liquid).
const BTN_PRIMARY   = 'btn btn--primary btn--animate bg-button text-button-text text-sm px-3 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase flex items-center justify-center gap-x-2 transition-colors duration-250 cursor-pointer';
const BTN_SECONDARY = 'btn btn--secondary btn--animate shadow-[inset_0_0_0_1px_var(--color-secondary-button-text)] text-secondary-button-text text-sm px-3 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase flex items-center justify-center gap-x-2 transition-colors duration-250 cursor-pointer';

// The theme heart (snippet/icon-pack), fill follows currentColor.
const HEART = `<svg xmlns="http://www.w3.org/2000/svg" style="width:1.05em;height:1.05em" viewBox="0 0 16 16" fill="none"><path d="M11.125 1.75C9.86937 1.75 8.755 2.24563 8 3.0975C7.245 2.24563 6.13062 1.75 4.875 1.75C3.78139 1.75132 2.73295 2.18635 1.95965 2.95965C1.18635 3.73295 0.751323 4.78139 0.75 5.875C0.75 10.3962 7.36312 14.0087 7.64437 14.1606C7.75367 14.2195 7.87586 14.2503 8 14.2503C8.12414 14.2503 8.24633 14.2195 8.35562 14.1606C8.63687 14.0087 15.25 10.3962 15.25 5.875C15.2487 4.78139 14.8137 3.73295 14.0404 2.95965C13.2671 2.18635 12.2186 1.75132 11.125 1.75ZM10.7819 10.6475C9.91142 11.3861 8.9809 12.0509 8 12.635C7.01909 12.0509 6.08858 11.3861 5.21812 10.6475C3.86375 9.48562 2.25 7.71375 2.25 5.875C2.25 5.17881 2.52656 4.51113 3.01884 4.01884C3.51113 3.52656 4.17881 3.25 4.875 3.25C5.9875 3.25 6.91875 3.8375 7.30562 4.78375C7.36193 4.92169 7.45805 5.03974 7.58172 5.12283C7.70539 5.20592 7.85101 5.2503 8 5.2503C8.14899 5.2503 8.29461 5.20592 8.41828 5.12283C8.54195 5.03974 8.63807 4.92169 8.69437 4.78375C9.08125 3.8375 10.0125 3.25 11.125 3.25C11.8212 3.25 12.4889 3.52656 12.9812 4.01884C13.4734 4.51113 13.75 5.17881 13.75 5.875C13.75 7.71375 12.1362 9.48562 10.7819 10.6475Z" fill="currentColor"/></svg>`;

/** The blue + Originals-image backdrop (image tinted via mix-blend-color, isolated).
 *  Starts invisible; the canvas fades opacity 0 → 1 on entering review. */
export function buildReviewBackdrop(catalog: Catalog): HTMLElement {
  const bg = el('div', 'cfg__review-bg absolute inset-0 z-0 isolate overflow-hidden pointer-events-none opacity-0 transition-opacity duration-500 ease-out');
  bg.style.background = REVIEW_BG; // base fill + fallback when no backdrop image
  // Desktop image (Step-3 setting → Originals fallback); mobile overrides desktop when set.
  const deskImg = catalog.reviewBackdrop || catalog.backdropImage;
  const deskFocal = catalog.reviewBackdrop ? catalog.reviewBackdropFocal : catalog.backdropFocal;
  const mobImg = catalog.reviewBackdropMobile || deskImg;
  const mobFocal = catalog.reviewBackdropMobile ? catalog.reviewBackdropMobileFocal : deskFocal;
  const layer = (vis: string, src: string, focal: string) =>
    `<img class="${vis} absolute inset-0 w-full h-full object-cover" style="object-position:${focal}" src="${src}" alt="">`;
  if (deskImg || mobImg) {
    bg.innerHTML =
      (deskImg ? layer('hidden md:block', deskImg, deskFocal) : '') +
      (mobImg ? layer('md:hidden', mobImg, mobFocal) : '') +
      `<span class="absolute inset-0 mix-blend-color" style="background-color:${REVIEW_BG}"></span>`;
  }
  return bg;
}

/** Top-left review copy: "YOUR CUSTOM {base}" + the "Edit design" underline link. */
export function buildReviewHeader(baseLabel: string, onEdit: () => void): HTMLElement {
  const head = el('div', 'cfg__review-head absolute top-0 left-0 z-[8] flex flex-col items-start gap-3 opacity-0 transition-opacity duration-300 ease-out pointer-events-none');
  const h = el('h1', 'h2 m-0 uppercase'); h.textContent = `Your custom ${baseLabel}`;
  const editBtn = el('button', 'cfg-edit-link text-sm font-semibold uppercase text-foreground leading-none cursor-pointer w-fit') as HTMLButtonElement;
  editBtn.type = 'button';
  editBtn.innerHTML = `<span>Edit design</span>`;
  editBtn.addEventListener('click', onEdit);
  head.append(h, editBtn);
  return head;
}

/** Decorative yellow paopao, bleeding in from the bottom. Fades in on review. */
export function buildReviewDecor(): HTMLElement {
  const decor = el('div', 'cfg__review-decor absolute bottom-[-11vw] left-[43vw] w-[28vw] rotate-[-31deg] -z-10 pointer-events-none opacity-0 transition-opacity duration-700 ease-out');
  decor.innerHTML = renderDecorIcon('paopao-two', '#FFCA22');
  return decor;
}

export interface OrderOverviewOpts {
  onAddToBag?: () => void | Promise<void>; // awaited so the button can show its loading state
  onSaveFavorite?: () => void | Promise<void>;
  mobile?: boolean;   // drawer layout: total + actions + trust pinned on top, items below
  bagLabel?: string;  // primary button label (e.g. "Update piece in bag" when editing a cart piece)
}

/** The "YOUR ORDER OVERVIEW" white card: scrolling line items (chain + grouped
 *  charms) with subtotal + actions pinned below. Built fresh on each review entry. */
export function buildOrderOverview(catalog: Catalog, build: BuildState, opts: OrderOverviewOpts = {}): HTMLElement {
  const chain = catalog.chains.find(c => c.id === build.chain?.id) ?? null;
  const chainLine = resolveChainLine(build);

  const mobile = !!opts.mobile;
  const card = el('div', mobile ? 'h-full flex flex-col' : 'bg-background rounded-sm p-4 h-full flex flex-col');
  const heading = el('h3', 'h3 m-0 mb-4 uppercase'); heading.textContent = 'Your order overview';

  // Only the line items scroll; subtotal/divider/buttons stay pinned. A soft fade hints
  // there's more when the list overflows. On desktop the configurator wrap is h-auto, so
  // cap the list to a viewport-relative height (minus the surrounding chrome) — otherwise a
  // long list grows the card and pushes the subtotal/Add-to-bag off-screen. Mobile scrolls
  // inside the fixed-height drawer, so it doesn't need the cap.
  const listWrap = el('div', `relative flex-1 min-h-0${mobile ? ' px-3' : ''}`);
  const list = el('div', `h-full overflow-y-auto cfg-drawer-scroll flex flex-col gap-2 pr-1${mobile ? ' pb-16' : ''}`);
  // Desktop: the configurator wrap is h-auto, so cap the list to the viewport minus the
  // surrounding chrome (nav + heading + subtotal/buttons) — inline so it never depends on a
  // Tailwind arbitrary-calc class emitting. Mobile scrolls inside the fixed-height drawer.
  if (!mobile) list.style.maxHeight = 'calc(100svh - 23rem)';
  const listFade = el('div', 'absolute inset-x-0 bottom-0 h-12 pointer-events-none bg-gradient-to-t from-background to-transparent opacity-0 transition-opacity duration-200');
  listWrap.append(list, listFade);

  if (chain) list.appendChild(lineCard({
    image: chain.closeUpImage || chain.image,
    cover: true,
    title: chain.title,
    subtitle: [chain.chainType, build.length != null ? `Chain length ${build.length}cm` : null],
    price: chainLine ? priceLabel(chainLine.price) : null,
  }));
  // Necklace per-cm surcharge as its own line so the itemisation matches the subtotal.
  const surcharge = lengthSurcharge(build);
  if (surcharge > 0) {
    const nkBase = build.chain?.lengthRules?.necklace?.base ?? 0;
    list.appendChild(lineCard({
      image: null, cover: false,
      title: 'Length surcharge',
      subtitle: [build.length != null && nkBase ? `${build.length - nkBase}cm over ${nkBase}cm` : null],
      price: priceLabel(surcharge),
    }));
  }
  for (const cl of aggregateCharms(build)) {
    const item = catalog.charms.find(ci => ci.productId === cl.productId) ?? null;
    const wp = item?.attributes.waterproof ? ', Waterproof' : '';
    list.appendChild(lineCard({
      image: item?.image ?? null,
      cover: false,
      title: cl.title,
      subtitle: [`${SIZE_WORD[item?.attributes.size ?? 'M']} charm${wp}`],
      price: priceLabel(cl.lineTotal),
      qty: `${cl.quantity} Piece${cl.quantity === 1 ? '' : 's'}`,
    }));
  }

  // Footer: subtotal → divider → actions → reassurance (line sits BELOW the subtotal).
  const foot = el('div', 'mt-4');
  const subtotal = el('div', 'flex items-center justify-between');
  subtotal.innerHTML = `<span class="h4 m-0 uppercase">${mobile ? 'Total' : 'Subtotal'}</span><span class="h4 m-0">${priceLabel(runningTotal(build))}</span>`;
  const divider = el('div', 'border-t border-detail/30 mt-4 mb-4');

  // Flex (not grid-cols-2) so the two buttons are equal width by default but each can grow
  // to fit its own (no-wrap) label, the other shrinking to compensate: flex-1 grows them
  // equally from a 0 basis, while the default min-width:auto floors each at its text width.
  const actions = el('div', 'flex gap-2');
  // Mirrors the Add-to-bag loading wipe (wipe "Save in favorites" up, "Saving" in greyed,
  // then back) and ignores re-clicks while the save+upload runs. Same look as the bag button.
  const saveBtn = makeLoadingButton(
    'Save in favorites',
    'btn btn--secondary btn--animate text-sm px-3 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase flex flex-1 whitespace-nowrap items-center justify-center gap-x-2 transition-colors duration-250 cursor-pointer',
    'shadow-[inset_0_0_0_1px_var(--color-secondary-button-text)] text-secondary-button-text',
    () => (opts.onSaveFavorite ?? (() => console.info('[configurator] save favorite — not implemented yet')))(),
    'Saving',
    HEART,
  );
  const bagBtn = makeLoadingButton(
    opts.bagLabel || 'Add to bag',
    'btn text-sm px-3 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase flex flex-1 whitespace-nowrap items-center justify-center cursor-pointer',
    'bg-button text-button-text',
    () => opts.onAddToBag?.(),
  );
  actions.append(saveBtn, bagBtn);

  const reassure = el('p', 'p text-foreground/50 text-center mt-3 mb-0 text-xs');
  reassure.textContent = 'Handmade to order · Shipped in 5 days · Secure checkout';

  if (mobile) {
    // Drawer layout: total + actions + trust + "Overview of items" pinned on top (visible
    // even when the drawer is closed); the line items scroll below when it's pulled up.
    const top = el('div', 'cfg__order-top shrink-0 px-3 pt-1');
    actions.classList.add('mt-3');                              // space between total and buttons
    reassure.classList.remove('mt-3'); reassure.classList.add('mt-2'); // a touch less to the trust line
    const itemsHeading = el('h3', 'h4 m-0 uppercase mb-3'); itemsHeading.textContent = 'Overview of items'; // space under title
    top.append(subtotal, actions, reassure, divider, itemsHeading);
    card.append(top, listWrap);
  } else {
    foot.append(subtotal, divider, actions, reassure);
    card.append(heading, listWrap, foot);
  }

  // Show the scroll fade only while there's more list below the fold.
  const updateFade = () => {
    const more = list.scrollHeight - list.scrollTop - list.clientHeight > 1;
    listFade.style.opacity = more ? '1' : '0';
  };
  list.addEventListener('scroll', updateFade);
  requestAnimationFrame(updateFade);

  return card;
}

/** A single order-overview row: thumbnail · title + subtitle · price (+ qty). */
function lineCard(o: {
  image: string | null; cover: boolean; title: string;
  subtitle: (string | null)[]; price: string | null; qty?: string;
}): HTMLElement {
  const row = el('div', 'flex items-center gap-3 rounded-xs bg-detail/10 p-3');
  const thumb = el('div', 'w-12 h-12 shrink-0 rounded-xs overflow-hidden flex items-center justify-center');
  if (o.image) {
    thumb.innerHTML = `<img class="w-full h-full ${o.cover ? 'object-cover' : 'object-contain'}" src="${o.image}" alt="">`;
  } else {
    thumb.innerHTML = `<span class="text-foreground/30 text-sm font-semibold">${initials(o.title)}</span>`;
  }
  const mid = el('div', 'flex-1 min-w-0');
  const subs = o.subtitle.filter(Boolean).map(s => `<div class="p text-foreground/60 text-sm leading-snug">${s}</div>`).join('');
  mid.innerHTML = `<div class="h5 m-0">${o.title}</div>${subs}`;
  const right = el('div', 'text-right shrink-0');
  right.innerHTML =
    (o.price ? `<div class="h5 m-0">${o.price}</div>` : '') +
    (o.qty ? `<div class="p text-foreground/60 text-sm">${o.qty}</div>` : '');
  row.append(thumb, mid, right);
  return row;
}
